<template>
  <b-form
    v-if="initialised"
    :key="`j${crud.data.id}-edit`"
    @keydown="form.errors.clear($event.target.name)"
    @submit.prevent="onSubmit"
  >
    <form-select
      v-model="form.brand_id"
      :auto-populate-if-single="true"
      :disabled="!creating"
      :options="
        allBrandData.length === 1
          ? allBrandData
          : [
              {
                id: null,
                name: 'Select a brand...',
              },
            ].concat(allBrandData)
      "
      :single-row="false"
      name="brand_id"
      @input="brandIdChanged"
    />

    <form-service-types-select
      v-model="form.service_types"
      :disabled="!form.brand_id"
      :filter-by-brand-ids="[form.brand_id]"
      :single-row="false"
      :show-prompts="true"
      name="service_types"
    />

    <!--Customer-->
    <form-customer-select
      v-model="form.customer"
      :readonly="!$can('update', 'jobs', 'customer')"
      :single-row="false"
      name="customer_id"
      @change="customerChanged"
    >
      <template v-if="$can('create', 'customers') || $can('update', 'customers')" #extra>
        <b-btn
          v-tooltip="{
            content: currentCustomerNotes,
            classes: ['badge-theme'],
          }"
          :disabled="currentCustomerNotes.length === 0"
          class="mr-2"
        >
          <font-awesome-icon :icon="['fal', 'info']" />
        </b-btn>
        <b-btn v-if="$can('create', 'customers')" variant="primary" @click.prevent="createCustomer">
          <font-awesome-icon :icon="['fal', 'plus-circle']" />
        </b-btn>
        <b-btn
          v-if="$can('update', 'customers')"
          :disabled="$lodash.get(form, 'customer_id') === null"
          class="ml-2"
          variant="secondary"
          @click.prevent="editCustomer"
        >
          <font-awesome-icon :icon="['fal', 'pencil']" />
        </b-btn>
      </template>
    </form-customer-select>
    <b-alert
      :show="
        crud.data.customer &&
        crud.data.customer.is_company === 1 &&
        crud.data.customer.payment_terms_approved === 0
      "
      variant="danger"
    >
      <font-awesome-icon :icon="['fas', 'info-circle']" class="mr-1" />
      Payment Terms Not Yet Approved for This Customer
    </b-alert>
    <div v-if="contactsInitialised && form.customer && form.customer.id">
      <div v-for="(contact, index) in form.contacts" :key="`j${crud.data.id}-contact-${index}`">
        <div class="row">
          <div class="col pr-1">
            <form-select
              v-model="contact.pivot.role"
              :labelless="true"
              :name="`contact_role_${index}`"
              :options="jobContactRoles"
              :single-row="true"
              @input="contactRoleChanged()"
            />
          </div>
          <div class="col pl-1 pr-0">
            <form-contact-select
              :key="`contact-${form.customer.id}-${index}`"
              :auto-init="true"
              :auto-populate-if-single="true"
              :customer="form.customer"
              :labelless="true"
              :name="`contact-${form.customer.id}-${index}-id`"
              :single-row="false"
              :value="contact"
              @change="contactChanged($event, index)"
            />
          </div>
          <div class="col-auto">
            <button class="btn btn-danger" type="button" @click.prevent="deleteContact(index)">
              <font-awesome-icon :icon="['fal', 'times']" />
            </button>
          </div>
        </div>
        <div class="row contact-detail mb-3" style="margin-top: -8px">
          <div class="col text-truncate">
            <a v-if="contact.phone" :href="`tel:${contact.phone}`" class="cursor ml-1 mr-1">
              <font-awesome-icon :icon="['fal', 'phone']" class="mr-1" />
              <phone-number :number="contact.phone" />
            </a>

            <a v-if="contact.mobile" :href="`tel:${contact.mobile}`" class="cursor ml-1 mr-1">
              <font-awesome-icon :icon="['fal', 'mobile']" class="mr-1" />
              <phone-number :number="contact.mobile" />
            </a>

            <a
              v-if="contact.email"
              class="cursor ml-1"
              href=""
              @click.prevent="chat(contact, 'mail')"
            >
              <font-awesome-icon :icon="['fal', 'envelope']" />
              <span
                v-tooltip="{
                  content: creating ? 'Please create the job first before contacting customer' : '',
                  classes: ['badge-theme'],
                }"
              >
                {{ contact.email }}
              </span>
            </a>
          </div>
        </div>
      </div>

      <button class="btn btn-primary btn-sm mb-3" type="button" @click.stop="addContact()">
        <font-awesome-icon :icon="['fal', 'plus-circle']" />
        Add Contact
      </button>
    </div>

    <form-address
      :key="`customer-${addressKey}-job-address`"
      v-model="form.job_address_id"
      :address="form.job_address"
      :addressable-id="$lodash.get(form, 'customer.id')"
      :auto-populate-if-single="true"
      :single-row="false"
      addressable-type="customer"
      error-name="job_address_id"
      name="job_address"
      @error="
        () => {
          handleAddressError('Job');
        }
      "
      @updateAddress="jobAddressChanged"
    >
      <template #extra>
        <button
          class="btn btn-secondary"
          type="button"
          @click.prevent="showBillingAddress = !showBillingAddress"
        >
          <font-awesome-icon v-if="showBillingAddress" :icon="['fal', 'chevron-up']" />
          <font-awesome-icon v-else :icon="['fal', 'chevron-down']" />
        </button>
      </template>
    </form-address>
    <div v-show="showBillingAddress">
      <form-address
        :key="`customer-${addressKey}-billing-address`"
        v-model="form.billing_address_id"
        :address="form.billing_address"
        :addressable-id="$lodash.get(form, 'customer.id')"
        :show-extra-slot="true"
        :show-reset-to-new-button="true"
        :single-row="false"
        addressable-type="customer"
        error-id="billing_address_id"
        name="billing_address"
        @error="
          () => {
            handleAddressError('Billing');
          }
        "
        @updateAddress="billingAddressChanged"
      />
    </div>

    <form-text
      v-if="$lodash.get(crud, 'data.customer.is_company', false)"
      v-model="form.po_reference"
      :single-row="false"
      name="po_reference"
    >
      Purchase Order Reference
    </form-text>

    <form-textarea
      v-model="form.description"
      :rows="3"
      :single-row="false"
      name="description"
    ></form-textarea>

    <div class="row">
      <div class="col">
        <form-select
          v-if="$store.state.jobs.foundViaOptions.regular.includes(crud.data.found_via)"
          v-model="form.found_via"
          :options="$store.state.jobs.foundViaOptions.regular"
          :single-row="false"
          name="found_via"
        />
        <form-text
          v-else
          v-model="form.found_via"
          :readonly="true"
          :single-row="false"
          name="found_via"
        />
      </div>
      <div class="col">
        <form-select
          v-model="form.status"
          :options="creating ? $store.state.jobs.jobStatusesCreate : $store.state.jobs.jobStatuses"
          :single-row="false"
          name="status"
          @input="maybeShowDormantReasonModal()"
        />
      </div>
    </div>

    <div class="row">
      <div class="col-9">
        <form-text
          v-if="
            form.brand_id &&
            $lodash.get(form, 'customer.picked_up_by_user.brands[0].id') &&
            form.customer.picked_up_by_user.brands[0].id === form.brand_id
          "
          v-model="form.customer.picked_up_by_user.name"
          :disabled="true"
          :single-row="false"
          name="po_reference"
        >
          Technician Booking
        </form-text>
        <template v-else>
          <form-user-select
            v-if="$can('update', 'jobs', 'referred_by')"
            v-model="form.referred_by"
            :single-row="false"
            name="referred_by"
            placeholder="Search technician..."
            user-role="technician"
            @change="updateReferredBy"
          />
          <form-text
            v-else-if="form.referred_by && form.referred_by.name"
            v-model="form.referred_by.name"
            :readonly="true"
            :single-row="false"
            name="referred_by"
          />
          <form-text
            v-else
            v-model="form.referred_by"
            :readonly="true"
            :single-row="false"
            name="referred_by"
          />
        </template>
      </div>
      <div class="col-3">
        <div class="form-component-row form-row">
          <label class="col-12 mb-2 text-right">Recurring</label>
          <div class="form-component-field col-12 mb-3 pr-2 text-right">
            <core-switch
              v-if="$can('update', 'jobs', 'is_recurring')"
              v-model="form.is_recurring"
              color="primary"
              off="N"
              on="Y"
              variant="3d"
              :disabled="true"
            />
            <div v-else class="pt-2">{{ form.is_recurring ? "Yes" : "No" }}</div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="
        $can('update', 'jobs', 'assigned_to') &&
        currentSelectedBrand &&
        currentSelectedBrand.job_assignable
      "
      class="row"
    >
      <div class="col">
        <form-user-select
          v-model="form.assigned_to"
          :single-row="false"
          name="assigned_to"
          placeholder="Search technician..."
          user-role="technician"
          @change="updateAssignedTo"
        />
      </div>
    </div>

    <div class="row">
      <div class="col-8">
        <button v-if="!creating" class="btn btn-success mr-1" @click.prevent="copyToClipboard">
          <font-awesome-icon :icon="['fal', 'paste']" />
        </button>

        <button v-if="canFollowUp" v-b-modal.follow-up class="btn btn-warning" @click.prevent>
          <font-awesome-icon :icon="['fal', 'headset']" />
          Follow Up
          {{
            crud.data.current_follow_up && crud.data.current_follow_up > 0
              ? `[${prettyFollowUpText}]`
              : ""
          }}
        </button>
      </div>
      <div class="col-4 text-right">
        <span
          v-tooltip="{
            content: saveDisabledReason(),
            classes: ['badge-theme'],
            offset: 7,
          }"
        >
          <button :disabled="!canSave" class="btn btn-primary">
            <font-awesome-icon v-if="form.busy" :icon="['fal', 'spinner']" spin />
            <font-awesome-icon v-if="!form.busy && creating" :icon="['fal', 'plus-circle']" />
            <font-awesome-icon v-if="!form.busy && !creating" :icon="['fal', 'pencil']" />
            {{ creating ? "Create" : "Save" }}
          </button>
        </span>
      </div>
    </div>

    <b-modal
      id="follow-up"
      ref="followUpModalRef"
      :hide-footer="true"
      size="lg"
      title="Add a Follow-up"
    >
      <b-form
        v-if="initialised"
        @keydown="followUpForm.errors.clear($event.target.name)"
        @submit.prevent="createOrUpdateFollowUp"
      >
        <div class="row">
          <div class="col">
            <b>Current Job Status:</b> {{ crud.data.status }}<br />
            <b>Follow-up Count:</b> {{ prettyFollowUpText }}<br />
            <br />
          </div>
          <div class="col text-right">
            <div v-if="parseInt(crud.data.current_follow_up, 10) > 2">
              <form-bootstrap-radio
                v-model="followUpForm.unreachable"
                :options="['Yes', 'No']"
                :single-row="false"
                buttons-class="mr-0"
                @input="followUpForm.errors.clear()"
              >
                Unreachable?
              </form-bootstrap-radio>
            </div>
          </div>
        </div>

        <template v-if="followUpForm.unreachable === 'No' && crud.data.current_follow_up < 6">
          <form-textarea
            v-model="followUpForm.detail"
            :actual-form="followUpForm"
            :rows="3"
            :single-row="false"
            name="detail"
            placeholder="Type a note here..."
          >
            Follow-up Notes
          </form-textarea>

          <div class="row">
            <div class="col">
              <form-bootstrap-radio
                v-model="followUpForm.reminder"
                :options="['Yes', 'No']"
                :single-row="false"
                @input="followUpForm.errors.clear()"
              >
                Create a reminder?
              </form-bootstrap-radio>
            </div>
          </div>

          <template v-if="followUpForm.reminder === 'Yes'">
            <form-user-select
              v-model="followUpForm.user"
              :actual-form="followUpForm"
              :single-row="false"
              name="user_id"
              placeholder="Search user..."
              user-role="admin"
              @change="updateAssignee"
            >
              Assigned To
            </form-user-select>

            <form-date
              v-model="followUpForm.due_date"
              :actual-form="followUpForm"
              :single-row="false"
              name="due_date"
              value-format="yyyy-MM-dd"
            />
          </template>
        </template>

        <div class="text-right">
          <button class="btn btn-primary">
            <template v-if="followUpForm.unreachable === 'Yes' || crud.data.current_follow_up >= 6">
              <font-awesome-icon :icon="['fal', 'pencil']" /> Update
            </template>
            <template v-else>
              <font-awesome-icon :icon="['fal', 'headset']" /> Add Follow-up
            </template>
          </button>
        </div>
      </b-form>
    </b-modal>

    <contact-customer ref="contactCustomer" :invisible="true" :job-id="currentJobId" />

    <b-modal
      id="customer-edit"
      :hide-footer="true"
      :no-close-on-backdrop="true"
      size="xxl"
      title="Customer"
    >
      <customer-edit
        ref="customerEditRef"
        :action="customerEditAction"
        :customer-id="customerEditId"
        @created="customerEditChanged"
        @updated="customerEditChanged"
      />
    </b-modal>

    <div v-if="creating || crud.data.status === 'Inquiry'" :key="promptUUID">
      <brand-prompt
        v-if="form?.brand_id && currentSelectedBrand?.prompt_text"
        :brand-name="currentSelectedBrand.name"
        :prompt-text="currentSelectedBrand.prompt_text"
      />

      <div v-if="currentSelectedServiceTypes.length > 0">
        <service-type-prompt
          v-for="serviceType in currentSelectedServiceTypes"
          :key="serviceType.id"
          :prompt-text="serviceType.prompt_text"
          :service-type-name="serviceType.name"
        />
      </div>
    </div>

    <b-modal
      id="dormant-reason"
      ref="dormantReasonModalRef"
      centered
      title="Marking Job as Dormant"
      @hide="submitting = false"
    >
      <p>Please select the reason for marking this job as dormant.</p>

      <fieldset>
        <b-form-radio
          v-for="reason in dormantReasonOptions"
          :key="reason.id"
          v-model="modalDormantReason"
          :value="reason.name"
          name="dormant_reason"
        >
          {{ reason.name }}
          <div class="text-muted pb-2 d-block">
            {{ reason.description }}
          </div>
        </b-form-radio>
      </fieldset>

      <template #modal-footer="{ cancel }">
        <div class="w-100">
          <b-button
            variant="primary"
            size="sm"
            class="float-right ml-2"
            :disabled="!modalDormantReason"
            @click="dormantReasonSelected"
          >
            Ok
          </b-button>
          <b-button variant="secondary" size="sm" class="float-right" @click="cancel">
            Cancel
          </b-button>
        </div>
      </template>
    </b-modal>
  </b-form>
</template>

<script type="text/babel">
import clone from "clone";
import debounce from "debounce";
import EventBus from "../../../imports/event-bus";
import ContactCustomer from "../../../components/common/ContactCustomer.vue";
import CustomerEdit from "./CustomerEdit.vue";
import BrandPrompt from "@/views/jobs/partials/BrandPrompt.vue";
import ServiceTypePrompt from "@/views/jobs/partials/ServiceTypePrompt.vue";
import format from "@/mixins/format";

const jobContactRoles = [
  "Job Contact",
  "Billing Contact",
  "Property Manager",
  "Tenant",
  "Site Manager",
  "Supervisor",
];
const addressDefault = {
  street: "",
  city: "",
  state: "",
  postcode: "",
  latitude: "",
  longitude: "",
  formatted: "",
};
const contactDefault = {
  customer_id: "",
  email: "",
  first_name: "",
  last_name: "",
  mobile: "",
  name: "",
  phone: "",
  pivot: {
    role: "Job Contact",
  },
};
const initialState = {
  found_via: "",
  assigned_to: "",
  assigned_to_id: "",
  referred_by: "",
  referred_by_id: "",
  customer: {},
  customer_id: "",
  contacts: [],
  brand_id: "",
  status: "Inquiry",
  description: "",
  job_address_id: "",
  billing_address_id: "",
  po_reference: "",
  service_types: [],
  is_recurring: null,
  dormant_reason: null,

  job_address: clone(addressDefault),
  billing_address: clone(addressDefault),
};

export default {
  components: {
    BrandPrompt,
    ServiceTypePrompt,
    ContactCustomer,
    CustomerEdit,
  },
  mixins: [format],

  props: {
    currentJobId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      initialised: false,
      promptUUID: "",
      allBrandData: [],
      contactsInitialised: false,
      creating: false,
      showBillingAddress: false,

      jobContactRoles,

      crud: null,
      form: new window.AppForm(initialState),

      followUpNote: null,
      followUpForm: new window.AppForm({
        detail: null,
        reminder: "No",
        user: "",
        user_id: "",
        due_date: "",
        unreachable: "No",
      }),

      customerEditId: null,
      customerEditAction: "create",

      submitting: false,
      modalDormantReason: null,
      dormantReasonOptions: [
        {
          id: "price",
          name: "Price Sensitivity",
          description: "Customer has declined because our quote is too high",
        },
        {
          id: "service_unavailable",
          name: "Service Unavailable",
          description: "The brand does not provide the service needed by the customer",
        },
        {
          id: "personal_circumstances",
          name: "Personal Circumstances",
          description:
            "Customer's personal reasons (e.g. health, emergencies) unrelated to the product, price, or service",
        },
        {
          id: "customer_unreachable",
          name: "Customer Unreachable",
          description: " Repeated efforts to call or email customer have yielded no response",
        },
      ],
    };
  },

  computed: {
    prettyFollowUpText() {
      switch (parseInt(this.crud.data.current_follow_up, 10)) {
        case 0:
          return "New Enquiry";

        case 1:
          return "1st Contact";

        case 2:
          return "2nd Contact";

        case 3:
          return "3rd Contact";
      }

      return "Unreachable";
    },

    canFollowUp() {
      return (
        this.canSave &&
        !this.creating &&
        ["Inquiry", "Quoted", "Invoiced"].includes(this.crud.data.status) &&
        this.$can("read", "jobs", "follow-ups")
      );
    },

    canSave() {
      // return !this.form.notReady() && this.form.brand_id && this.form.job_contact_id;
      return !this.form.notReady() && this.form.brand_id;
    },

    addressKey() {
      if (this.$lodash.has(this.form, "customer.id")) {
        return this.$lodash.get(this.form, "customer.id");
      }

      return window.generateUUID();
    },

    currentSelectedBrand() {
      if (!this.$lodash.get(this, "form.brand_id")) {
        return null;
      }

      return this.$lodash.find(
        this.allBrandData,
        (brand) => brand.id === parseInt(this.form.brand_id)
      );
    },

    currentSelectedServiceTypes() {
      const serviceTypes = clone(this.$lodash.get(this, "form.service_types", []));
      return serviceTypes.reverse();
    },

    currentCustomerNotes() {
      let notes = [];

      if (!this.$lodash.get(this, "form.customer.id")) {
        notes = [];
      }

      notes = this.$lodash.get(this, "form.customer.notes", []);

      if (notes.length === 0) {
        return "";
      }

      const result = notes
        .map((note) => {
          const time = this.formatLocalDate(note.created_at);
          return `<div class="mb-2">${note.detail} at ${time}</div>`;
        })
        .join("");

      return `<div class="-mb-2">${result}</div>`;
    },
  },

  /* eslint-disable object-shorthand, func-names, prefer-arrow-callback */
  watch: {
    currentJobId() {
      // Browser Title
      // noinspection JSUnresolvedFunction
      this.$root.setTitle(`Job #${this.currentJobId}`);

      this.$store.commit("tech-availability/stopFiltering");
      this.init();
    },
    "form.brand_id"(brandId) {
      this.$store.commit("tech-availability/setHiddenFilter", {
        type: "brand_id",
        value: brandId,
      });

      this.$store.dispatch("tech-availability/filter");
    },
    "form.service_types"(serviceTypes) {
      EventBus.$emit("service-type-changed", serviceTypes);
      EventBus.$emit("regenerate-prompts");

      this.$store.commit("tech-availability/setHiddenFilter", {
        type: "service_types",
        value: serviceTypes.map((serviceType) => serviceType.id),
      });

      this.$store.dispatch("tech-availability/filter");
    },
    "form.job_address.postcode": debounce(function (postcode) {
      if (postcode && postcode.length === 4) {
        this.$store.commit("tech-availability/setHiddenFilter", {
          type: "postcode",
          value: postcode,
        });
      } else {
        this.$store.commit("tech-availability/setHiddenFilter", {
          type: "postcode",
          value: null,
        });
      }

      this.$store.dispatch("tech-availability/filter");
    }, 500),
  },

  async mounted() {
    this.promptUUID = window.generateUUID();

    EventBus.$on("appointment-created", (appointment) => {
      // Because apponitment is created on an inquiry job
      // We need to refresh job data since it's now in "scheduled" status
      if (this.form.status === "Inquiry") {
        this.init();
        this.$store.dispatch("jobs/refreshJob", appointment.job_id);
      }
    });

    EventBus.$on("customer-created", (payload) => {
      this.crud.data[payload.type] = payload.customer;
      this.form[`${payload.type}_id`] = payload.customer.id;
    });

    EventBus.$on("chating-with-contact", (payload) => {
      if (this.$refs.contactCustomer) {
        this.$refs.contactCustomer.setCustomer(payload.customer);
        this.$refs.contactCustomer.setBrandId(payload.brand_id);
        this.$refs.contactCustomer.setContact(payload.contact);
        this.$refs.contactCustomer.setChannel(payload.channels);

        this.$refs.contactCustomer.showModal();
      }
    });

    EventBus.$on("job-edit-customer-change", (customer) => {
      this.customerChanged(customer);

      this.$bvModal.hide("customer-edit");
    });

    EventBus.$on("to-refresh-job", () => {
      this.init();
    });

    EventBus.$on("regenerate-prompts", () => {
      window.toastr.remove();
      this.promptUUID = window.generateUUID();
    });

    await this.$store.dispatch("jobs/initialise");
    this.allBrandData = await this.$store.dispatch("brands/getData");
    const currentUser = this.$auth.user();
    if (
      currentUser?.designation?.restricted_to_brands ||
      currentUser?.designation?.restricted_to_state_brands
    ) {
      this.allBrandData = currentUser.brands;
    }

    this.init();
  },

  methods: {
    init() {
      this.crud = new window.AppCrud(
        "job",
        [
          "appointments.user",
          "brand",
          "customer.primaryContact",
          "customer.pickedUpByUser.brands",
          "contacts",
          "jobContacts",
          "jobAddress",
          "billingAddress",
          "serviceTypes",
          "assignedTo",
          "referredBy",
        ],
        "edit",
        "/jobs",
        "&with_recent_customer_notes=true"
      );

      // Browser Title
      // noinspection JSUnresolvedFunction
      this.$root.setTitle(`Job #${this.currentJobId}`);

      this.initFollowUp();
      this.resetAddresses();

      this.submitting = false;
      this.modalDormantReason = null;

      return this.crud.initFormData(this.form, this.currentJobId).then(() => {
        // Detect if this is a brand-new job just being created
        // Set to Inquiry if it's at default state Draft
        if (this.form.status === "Draft") {
          this.form.status = "Inquiry";
          this.creating = true;
        } else {
          this.creating = false;
        }

        this.form.assigned_to_id = "";
        this.form.referred_by_id = "";
        this.form.is_recurring = this.crud.data.is_recurring > 0;

        this.postInit();

        this.initialised = true;
      });
    },

    postInit() {
      // Init Follow-up related values
      this.followUpNote = null;
      this.followUpForm.unreachable = this.crud.data.current_follow_up >= 6 ? "Yes" : "No";

      // Add default contact if empty
      if (this.form.contacts && this.form.contacts.length === 0) {
        this.addContact();
      }

      // Determine if current operation should drive Availability table's filtering
      if (this.creating) {
        this.$store.commit("tech-availability/startFiltering");
      } else if (this.$store.state.jobs.currentTab !== 1) {
        this.$store.commit("tech-availability/stopFiltering");
      }

      // Initialise default job/billing address if null
      if (this.form.job_address === null) {
        this.form.job_address = clone(addressDefault);
      }
      if (this.form.billing_address === null) {
        this.form.billing_address = clone(addressDefault);
      }

      // Check job_address_id/billing_address_id to determine if to show/hide billing address
      this.showBillingAddress = this.form.job_address_id !== this.form.billing_address_id;

      // Set job in state
      this.$store.commit("jobs/setCurrentJob", this.crud.data);

      // Initial hidden filter
      this.$store.commit("tech-availability/setHiddenFilters", {
        brand_id: this.form.brand_id,
        postcode: this.$lodash.get(this.form, "job_address.postcode", null),
        service_types: this.form.service_types.map((serviceType) => serviceType.id),
      });

      this.initContacts();

      if (this.crud.data.status !== "Inquiry") {
        window.toastr.remove();
      }
    },

    initContacts() {
      // Rearrange contacts in preferred order
      if (this.$lodash.has(this.form, "contacts[0].pivot.role")) {
        this.contactsInitialised = false;
        this.form.contacts.sort(
          (a, b) =>
            this.jobContactRoles.indexOf(a.pivot.role) - this.jobContactRoles.indexOf(b.pivot.role)
        );
        this.contactsInitialised = true;
      }
    },

    rerender() {
      this.initialised = false;
      this.$nextTick(() => {
        this.initialised = true;
      });
    },

    resetAddresses() {
      this.form.job_address = clone(addressDefault);
      this.form.job_address_id = "";
      this.form.billing_address = clone(addressDefault);
      this.form.billing_address_id = "";

      this.showBillingAddress = this.form.job_address_id !== this.form.billing_address_id;
    },

    maybeShowDormantReasonModal() {
      if (this.form.status !== "Dormant") {
        this.modalDormantReason = null;
        this.form.dormant_reason = null;
        return;
      }

      if (this.crud.data.status !== "Dormant" && !this.form.dormant_reason) {
        this.$refs.dormantReasonModalRef.show();
      }
    },

    dormantReasonSelected() {
      this.form.dormant_reason = this.modalDormantReason;

      if (this.submitting) {
        this.onSubmit();
      }

      this.$refs.dormantReasonModalRef.hide();
    },

    onSubmit() {
      this.submitting = true;

      this.maybeShowDormantReasonModal();

      if (this.form.status === "Dormant" && !this.form.dormant_reason) {
        // Will not allow save if dormant reason is not selected on Dormant jobs
        return;
      }

      if (!this.$lodash.get(this.crud, "data.customer.is_company", false)) {
        // Shouldn't have PO Reference if customer is not a company
        this.form.po_reference = "";
      }

      if (this.$lodash.has(this.form, "customer.picked_up_by_user.id")) {
        // The current customer booked by a tech, so we will nullify the referred by value
        this.form.referred_by = null;
        this.form.referred_by_id = "";
      } else if (!this.form.referred_by_id && this.$lodash.has(this.form, "referred_by.id")) {
        // The current custoemr is not booked by any tech, assign referred by id from selected referred by tech
        this.updateReferredBy(this.form.referred_by);
      }

      // Populate assigned to value
      this.updateAssignedTo(this.form.assigned_to);

      this.crud.submit(this.form).then(() => {
        this.init().then(() => {
          this.$store.commit("jobs/updateRecord", this.crud.data);
          this.$store.dispatch("tech-availability/reload");
        });

        window.toastr.success("Job updated!", "Success");

        EventBus.$emit("job-updated");
      });
    },

    initFollowUp() {
      this.followUpForm.reset();

      this.followUpForm.user = this.$auth.user() || {};
      this.followUpForm.user_id = this.followUpForm.user.id;
      this.followUpForm.reminder = "No";
      this.followUpForm.unreachable = "No";
    },

    async createOrUpdateFollowUp() {
      if (this.followUpForm.unreachable === "Yes" || this.crud.data.current_follow_up >= 6) {
        await this.followUpForm.put(`/jobs/${this.crud.data.id}/follow-up`);
        window.toastr.success("Follow-up updated!", "Success");
      } else {
        await this.followUpForm.post(`/jobs/${this.crud.data.id}/follow-up`);
        window.toastr.success("Follow-up added!", "Success");
      }

      this.initFollowUp();
      this.init().then(() => {
        this.$store.commit("jobs/updateRecord", this.crud.data);
        this.$store.dispatch("tech-availability/reload");
      });
      EventBus.$emit("job-updated");
      this.$refs.followUpModalRef.hide();
    },

    updateAssignee(user) {
      this.followUpForm.user = user;
      this.followUpForm.user_id = user.id;
      this.followUpForm.errors.clear("user_id");
    },

    /* eslint-disable no-unused-vars */
    customerChanged(customer) {
      if (!this.form.customer || this.form.customer.id !== customer.id) {
        // Fire event
        EventBus.$emit("job-edit-customer-changed", customer);

        // Assign the customer to the newly selected
        this.form.customer = customer;
        this.form.customer_id = customer.id;
        this.form.customer.id = customer.id;

        // Reset contacts, job_address and billing_address
        this.form.contacts = [];
        this.addContact();
        this.resetAddresses();

        this.form.errors.clear("customer_id");
      }
    },

    jobAddressChanged(address) {
      this.form.job_address = address;
      this.form.job_address_id = address.id;
    },

    billingAddressChanged(address) {
      this.form.billing_address = address;
      this.form.billing_address_id = address.id;
    },

    contactChanged(contact, index) {
      if (!contact) {
        return;
      }

      const update = contact;
      update.pivot = {
        ...this.form.contacts[index].pivot,
        ...{
          contact_id: contact.id,
        },
      };

      this.form.contacts[index] = { ...this.form.contacts[index], ...update };

      this.clearCustomerRelatedErrors();
    },

    contactRoleChanged() {
      this.clearCustomerRelatedErrors();
    },

    clearCustomerRelatedErrors() {
      this.form.errors.clear("customer_id");
      this.form.contacts.forEach((contact, index) => {
        this.form.errors.clear(`contact_role_${index}`);
      });
    },

    addContact() {
      this.form.contacts.push(window.clone(contactDefault));
    },

    deleteContact(index) {
      this.form.contacts.splice(index, 1);
      this.form.errors.clear();
    },

    createCustomer() {
      this.customerEditId = null;
      this.customerEditAction = "create";

      this.$nextTick(() => {
        this.$bvModal.show("customer-edit");
      });

      // window.open('/customers/create');
    },

    editCustomer() {
      if (this.$lodash.has(this.form, "customer.id")) {
        // Code to use modal for customer edit
        // this.customerEditId = this.form.customer_id;
        // this.customerEditAction = 'edit';
        //
        // this.$nextTick(() => {
        //   this.$bvModal.show('customer-edit');
        // });

        window.open(`/customers/${this.form.customer.id}/edit`);
      }
    },

    customerEditChanged(customer) {
      this.form.contacts = [];
      this.contactsInitialised = true;

      const newCustomer = window.clone(customer);
      delete newCustomer.contacts;

      this.form.customer = newCustomer;
      this.form.customer_id = newCustomer.id;
      this.form.job_address = null;
      this.form.billing_address = null;
      this.form.job_address_id = null;
      this.form.billing_address_id = null;

      this.$nextTick(() => {
        this.postInit();
        this.rerender();

        this.$bvModal.hide("customer-edit");
      });
    },

    chat(contact, channels) {
      if (this.creating) {
        return;
      }

      EventBus.$emit("chating-with-contact", {
        customer: this.form.customer,
        brand_id: this.form.brand_id,
        contact,
        channels,
      });
    },

    handleAddressError(type) {
      window.toastr.warning(
        `Address may be incorrectly parsed, please double check the exact ${type} address.`,
        "Warning"
      );
    },

    saveDisabledReason() {
      if (!this.form.brand_id) {
        return "Please select a brand first";
      }

      if (!this.form.customer_id) {
        return "Please specify the customer";
      }

      const jobContact = this.form.contacts.find((contact) => contact.pivot.role === "Job Contact");
      if (!jobContact?.id) {
        return "Please specify the job contact";
      }

      if (this.form.errors.any()) {
        return "The form has validation error";
      }

      if (this.form.busy) {
        return "Form is busy processing";
      }

      return null;
    },

    brandIdChanged(brand_id) {
      if (typeof brand_id === "object" && brand_id !== null) {
        // auto-populate-if-single for brand select here return the brand object
        // so parse it first
        this.form.brand_id = brand_id.id;
      } else {
        this.form.brand_id = brand_id;
      }

      this.form.service_types = [];
    },

    copyToClipboard() {
      let details = `#${this.crud.data.id}J\n\n`;

      if (this.$lodash.get(this.form, "customer.is_company")) {
        details += `Company: ${this.$lodash.get(this.form, "customer.company_name")}\n\n`;
      }

      this.form.contacts.forEach((contact) => {
        details += `${contact.pivot.role}: ${contact.name}\n` + `${contact.email}\n\n`;
      });

      if (this.$lodash.has(this.form, "job_address.shortAddress")) {
        details += `Job Address: ${this.$lodash.get(this.form, "job_address.shortAddress")}\n`;
      }
      if (this.$lodash.has(this.form, "billing_address.shortAddress")) {
        details += `Billing Address: ${this.$lodash.get(
          this.form,
          "billing_address.shortAddress"
        )}\n`;
      }

      navigator.clipboard.writeText(details);
      window.toastr.info("Job details copied to clipboard!", "Success");
    },

    updateReferredBy(user) {
      this.form.referred_by = user;
      this.form.referred_by_id = user ? user.id : null;
      this.form.errors.clear("referred_by");
    },

    updateAssignedTo(user) {
      this.form.assigned_to = user;
      this.form.assigned_to_id = user ? user.id : null;
      this.form.errors.clear("assigned_to");
    },
  },
};
</script>
